import { formatKeyInput, isCompleteKey } from '@iqrapath/shared'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Footprints } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError, redeem } from '../../lib/api.ts'
import { ME_KEY } from '../../lib/queries.ts'
import { useSky } from '../../lib/useSky.ts'
import { Island } from '../home/Island.tsx'
import { SkyBackdrop } from '../home/SkyBackdrop.tsx'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function LoginPage() {
  const sky = useSky()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [key, setKey] = useState('')
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)
  const [wait, setWait] = useState(0)
  const autoRetried = useRef(false)
  const magicHandled = useRef(false)

  const submit = useCallback(
    async (e: string, k: string) => {
      setError('')
      if (!EMAIL_RE.test(e.trim())) return setError('Isi email yang kamu pakai saat membeli.')
      if (!isCompleteKey(k)) return setError('Kodenya belum lengkap. Formatnya empat kelompok, misalnya A7K2-M9P4-X3R8-B5N2.')
      setPending(true)
      try {
        await redeem(e.trim(), k)
        await qc.invalidateQueries({ queryKey: ME_KEY })
        navigate('/', { replace: true })
      } catch (err) {
        if (err instanceof ApiError && err.status === 429) setWait(err.retryAfter ?? 60)
        setError(err instanceof ApiError ? err.message : 'Terjadi masalah. Coba lagi nanti.')
      } finally {
        setPending(false)
      }
    },
    [navigate, qc],
  )

  // "Masuk otomatis" link from the license email: /masuk?e=<email>&k=<key>. Read once, then removed from the URL/history.
  useEffect(() => {
    if (magicHandled.current) return
    magicHandled.current = true
    const p = new URLSearchParams(window.location.search)
    const e = p.get('e')?.trim() ?? '', k = formatKeyInput(p.get('k') ?? '')
    if (!p.has('e') && !p.has('k')) return
    window.history.replaceState(null, '', window.location.pathname)
    if (e) setEmail(e)
    if (k) setKey(k)
    if (e && isCompleteKey(k)) void submit(e, k)
  }, [submit])

  // 429 from CloudPass: count down, then retry once automatically (docs/UX-PLAYBOOK.md §6.1).
  useEffect(() => {
    if (wait <= 0) return
    const id = window.setTimeout(() => {
      if (wait === 1 && !autoRetried.current) {
        autoRetried.current = true
        setWait(0)
        void submit(email, key)
      } else setWait(wait - 1)
    }, 1000)
    return () => window.clearTimeout(id)
  }, [wait, email, key, submit])

  const onSubmit = (ev: FormEvent) => { ev.preventDefault(); void submit(email, key) }
  const busy = pending || wait > 0
  const lit = sky.phase === 'isya' || sky.phase === 'maghrib'

  return (
    <div className="login">
      <header className="hero" data-tone={sky.phase === 'siang' ? 'light' : 'dark'}>
        <SkyBackdrop phase={sky.phase} />
        <div className="login-brand">
          <span className="mark" aria-hidden="true"><Footprints size={18} /></span> IqraPath
        </div>
        <div className="stage"><Island phase={sky.phase} lit={lit} /></div>
      </header>

      <section className="login-sheet">
        <h1>Satu langkah kecil, <em>setiap hari</em>.</h1>
        <p className="muted" style={{ marginTop: 8 }}>
          Masuk dengan email yang kamu pakai saat membeli dan kode dari email kami.
        </p>

        <form onSubmit={onSubmit} noValidate>
          <label className="field">
            <span>Email</span>
            <input
              className="input" type="email" name="email" autoComplete="email" inputMode="email" placeholder="nama@gmail.com"
              value={email} onChange={(e) => { setEmail(e.target.value); autoRetried.current = false }}
              aria-invalid={Boolean(error) && !EMAIL_RE.test(email.trim())}
            />
          </label>
          <label className="field">
            <span>Kode lisensi</span>
            <input
              className="input mono" name="key" autoComplete="off" autoCapitalize="characters" spellCheck={false}
              placeholder="XXXX-XXXX-XXXX-XXXX" value={key}
              onChange={(e) => { setKey(formatKeyInput(e.target.value)); autoRetried.current = false }}
              aria-invalid={Boolean(error) && !isCompleteKey(key)}
              aria-describedby="key-help"
            />
          </label>
          <p className="help" id="key-help">Cukup sekali. Setelah itu kamu tetap masuk di perangkat ini.</p>

          <p className="error" role="alert">{error}</p>

          <button className="btn btn-primary btn-block" type="submit" disabled={busy} style={{ marginTop: 10, minHeight: 54 }}>
            {pending ? <><span className="spinner" aria-hidden="true" /> Memeriksa…</>
              : wait > 0 ? `Coba lagi dalam ${wait} dtk`
              : <>Masuk <ArrowRight size={18} aria-hidden="true" /></>}
          </button>
        </form>

        <p className="foot">Belum menerima kode? Cek folder spam, atau hubungi admin lewat WhatsApp.</p>
      </section>
    </div>
  )
}
